<template>
    <tab-section title="Information" :class="{'lam-lower-contrast': !!wizardShown}">
        <template v-slot:information>
            <attachment-image
                v-if="!!record"
                entity="project"
                owner-type="App\Project"
                :owner-id="record.id"
                :editable="editable"
            />
        </template>

        <div class="uk-grid uk-grid-small">
            <div v-if="number_editable" class="uk-width-1-5">
                <div class="uk-form-controls-text uk-text-bold">Number</div>
                <form-input
                    id="number"
                    v-model="record.number"
                    name="number"
                    :error="has_error('number')"
                    :required="false"
                    :editable="true"
                    width="1-1"
                    placeholder="Project number"
                    @input="save_field"
                    class="uk-width-1-1"
                />
            </div>
            <div :class="{'uk-width-2-5': number_editable, 'uk-width-1-2': !number_editable}">
                <div class="uk-form-controls-text uk-text-bold">Project name</div>
                <form-input
                    id="name"
                    v-model="record.name"
                    name="name"
                    :error="has_error('name')"
                    :required="true"
                    :editable="editable"
                    width="1-1"
                    placeholder="Project name"
                    @input="save_field"
                    class="uk-width-1-1"
                />
            </div>
            <div :class="{'uk-width-2-5': number_editable, 'uk-width-1-2': !number_editable}">
                <div class="uk-form-controls-text uk-text-bold">Scope name</div>
                <form-input
                    id="nickname"
                    v-model="record.nickname"
                    name="nickname"
                    :error="has_error('nickname')"
                    :required="false"
                    :editable="editable"
                    width="1-1"
                    placeholder="Scope name (nickname)"
                    @input="save_field"
                    class="uk-width-1-1"
                />
            </div>
            <div class="uk-width-1-3">
                <div class="uk-form-controls-text uk-text-bold">Studio(s)</div>
                <div class="uk-grid uk-grid-small">
                    <div v-for="studio in raw_studio_records" :key="studio.id" class="uk-width-1-3 uk-form-controls-text">
                        <form-checkbox
                            :id="`studio-${studio.id}`"
                            :value="record.studio_ids.indexOf(studio.id)>=0"
                            name="studio_ids"
                            :editable="editable"
                            @input="toggle_studio(studio.id, $event)"
                        >
                            {{ studio.name }}
                        </form-checkbox>
                    </div>
                </div>
            </div>

            <div v-if="!project_is_overhead(record)" class="uk-width-1-3">
                <div :class="{'uk-text-required': !pic||!pic.id}" class="uk-form-controls-text uk-text-bold">
                    <open-icon v-if="!pic||!pic.id" glyph="exclamation-triangle" />
                    PIC
                </div>
                <form-select-user
                    id="pic_id"
                    name="pic_id"
                    :value="record.pic_id"
                    :filter="pics_filter"
                    :editable="editable"
                    :required="true"
                    class="uk-width-1-1"
                    @input="change_team"
                />
            </div>
            <div class="uk-width-1-3">
                <div class="uk-form-controls-text uk-text-bold">PM</div>
                <form-select-user
                    id="pm_id"
                    name="pm_id"
                    :value="record.pm_id||0"
                    :filter="pms_filter"
                    empty-label="(No PM)"
                    :editable="editable"
                    :required="false"
                    class="uk-width-1-1"
                    @input="change_team"
                />
            </div>
            <div class="uk-width-1-1">
                <div class="uk-form-controls-text uk-text-bold">Designer(s)</div>
                <div class="lam-designer-pickers uk-margin-small-top">
                    <div
                        v-for="designer_id in sorted_designer_ids"
                        :key="designer_id"
                        class="uk-button-group lam-designer-picker"
                    >
                        <base-button
                            class="lam-designer-picker-remove"
                            @click.prevent="remove_designer(designer_id)"
                        >
                            <open-icon glyph="close" />
                        </base-button>
                        <base-button
                            :disabled="true"
                            class="lam-designer-picker-initials"
                        >
                            {{ designer_by_id(designer_id).initials }}
                        </base-button>
                        <base-button
                            class="lam-designer-picker-star"
                            @click.prevent="designer_toggle_star(designer_id)"
                        >
                            <open-icon :glyph="project_user_by_id(designer_id).starred ? 'star' : 'star-o'" />
                        </base-button>
                        <dropdown-button
                            data-uk-dropdown="{pos:'bottom-right'}"
                            class="lam-designer-picker-dropdown"
                        >
                            <template v-slot:dropdown>
                                <ul class="uk-nav uk-nav-dropdown">
                                    <li
                                        v-for="designer in available_designers(designer_id)"
                                        :key="designer.id"
                                    >
                                        <a href @click.prevent="change_team(designer.id, 'designer_ids', designer_id)">
                                            {{ designer.initials }}
                                        </a>
                                    </li>
                                </ul>
                            </template>
                            <open-icon glyph="chevron-down" />
                        </dropdown-button>
                    </div>
                    <div
                        v-if="available_designers(0).length"
                        class="uk-button-group lam-designer-picker"
                    >
                        <dropdown-button
                            data-uk-dropdown="{pos:'bottom-right'}"
                            class="lam-designer-picker-dropdown"
                        >
                            <template v-slot:dropdown>
                                <ul class="uk-nav uk-nav-dropdown">
                                    <li
                                        v-for="designer in available_designers(0)"
                                        :key="designer.id"
                                    >
                                        <a href @click.prevent="change_team(designer.id, 'designer_ids')">
                                            {{ designer.initials }}
                                        </a>
                                    </li>
                                </ul>
                            </template>
                            <open-icon glyph="plus" />
                        </dropdown-button>
                    </div>
                </div>
                <!--
                <div class="uk-form-controls-text uk-text-bold">Designers</div>
                <form-tags-input
                    id="designer_ids"
                    name="designer_ids"
                    entity="user"
                    :value="record.designer_ids||[]"
                    :local-options="filtered_designers"
                    label-field="initials"
                    :editable="editable"
                    :required="false"
                    class="uk-width-1-1"
                    @input="change_team"
                >
                    <template v-slot:selected-option="{ option }">
                        <span>{{ designer_by_id(option).initials }}</span>
                        <base-button
                            flavor="link"
                            size="small"
                            @click.prevent="designer_toggle_star(option)"
                        >
                            <open-icon :glyph="project_user_by_id(option).starred ? 'star' : 'star-o'" />
                        </base-button>
                    </template>
                </form-tags-input>
                //-->
            </div>
        </div>

        <div v-if="!!record" class="uk-grid uk-grid-small lam-status-grid">
            <div class="uk-width-1-1">
                <div id="status-picker" class="uk-form-controls-text uk-text-bold uk-width-1-1">
                    <a
                        v-if="record.is_latest_project && (is_business_manager || is_db_admin_active)"
                        href
                        title="Undo this project's activation - make it a Prospective Project again"
                        class="uk-button uk-button-mini uk-button-primary"
                        @click.prevent="deactivate"
                    >
                        <open-icon glyph="undo" />
                    </a>
                    Status
                </div>
                <form-select
                    id="project_status"
                    name="project_status"
                    :value="record.project_status"
                    :options="project_statuses"
                    id-field="code_constant"
                    :editable="status_editable"
                    :required="true"
                    class="uk-width-1-1"
                    @input="change_status"
                >
                    <template v-slot:read_only>
                        {{ project_status_label(record.project_status) }}
                    </template>
                </form-select>
                <div
                    v-if="!!record && !!record.project_status && !record.project_status.match(/^(pending)$/)"
                    class="uk-width-1-1 uk-margin-top"
                >
                    <form-text-area
                        v-if="status_editable && !!record && !!record.project_status && !record.project_status.match(/^(active)$/)"
                        id="project_status_notes"
                        entity="project"
                        name="project_status_notes"
                        v-model="record.project_status_notes"
                        placeholder="Please explain..."
                        width="1-1"
                        :required="false"
                        @input="save_field"
                    />
                    <p v-else-if="!!record.project_status_notes" class="uk-text-center">{{ record.project_status_notes }}</p>
                </div>
            </div>
        </div>

        <form-checkbox
            v-if="!!record && !!record.project_status.match(/^(active)$/)"
            id="ignore_on_reports"
            v-model="record.ignore_on_reports"
            name="ignore_on_reports"
            :editable="is_db_admin_active"
            :required="false"
            @input="save_field"
            class="uk-display-inline-block uk-margin-top"
        >
            Ignore in reports
        </form-checkbox>

        <modal-dialog
            id="status-change-confirmation"
            :show.sync="confirming_status_change"
        >
            <template v-slot:title>
                Change status to "{{ project_status_label(status_change_to) }}"
            </template>

            <form class="uk-form" @submit.prevent>
                <h5>Are you sure you want to change this project's status to "{{ project_status_label(status_change_to) }}"?</h5>
                <p class="uk-text-muted">NOTE: You might not be able to undo this change, and you might lose access to the project as a result...</p>

                <div v-if="(status_change_to==='active') && !!record && (!record.scopes_data || !record.scopes_data.scopes || !!all_other_team_project_ids.length)">
                    <form-checkbox
                        v-if="!record.scopes_data || !record.scopes_data.scopes"
                        id="activate_as_scope"
                        v-model="activate_as_scope"
                        name="activate_as_scope"
                        :editable="true"
                    >
                        Activate as a scope of an existing project...
                    </form-checkbox>
                    <div v-if="activate_as_scope" class="uk-margin-small-top uk-padding-left">
                        <div class="uk-flex uk-flex-center" style="align-items: flex-end;">
                            <div class="uk-flex-item-1-1">
                                <div class="uk-form-controls-text uk-text-bold">Existing Project Number</div>
                                <form-input
                                    id="scope_number"
                                    v-model="scope_number"
                                    name="scope_number"
                                    placeholder="00000"
                                    :required="true"
                                    :editable="true"
                                    width="1-1"
                                    class="uk-width-1-1"
                                />
                            </div>
                            <label class="uk-flex-item-none uk-margin-small-left uk-margin-small-right">.</label>
                            <div class="uk-flex-item-none">
                                <div class="uk-form-controls-text uk-text-bold">New Scope's Suffix</div>
                                <form-input
                                    id="scope_suffix"
                                    v-model="scope_suffix"
                                    name="scope_suffix"
                                    placeholder="10"
                                    :required="true"
                                    :editable="true"
                                    width="1-5"
                                    class="uk-width-1-1"
                                />
                            </div>
                        </div>
                        <div v-if="has_error('scope_number')" class="uk-form-help-block uk-text-center uk-text-danger">
                            {{ has_error('scope_number') }}
                        </div>
                        <div v-else class="uk-form-help-block uk-text-center uk-text-muted">
                            (Leave the suffix field empty and it will be automatically assigned)
                        </div>
                    </div>
                    <div v-if="all_other_team_project_ids.length" class="uk-margin-top">
                        <div class="uk-form-controls-text uk-text-bold">Close Other Team Projects:</div>
                        <ul class="uk-list uk-list-line">
                            <li
                                v-for="id in all_other_team_project_ids"
                                :key="id"
                            >
                                <form-checkbox
                                    id="other_team_project_ids"
                                    :value="other_team_project_ids.indexOf(id) >= 0"
                                    name="other_team_project_ids"
                                    :editable="true"
                                    @input="toggle_other_team_project_id(id)"
                                >
                                    <span v-if="!!project_by_id(id).number">{{ project_by_id(id).number }} </span>{{ project_by_id(id).nickname || project_by_id(id).name }}
                                    <div v-if="!!project_by_id(id).client_name" class="uk-text-small">{{ project_by_id(id).client_name }}</div>
                                </form-checkbox>
                            </li>
                        </ul>
                    </div>
                </div>
                <div v-else-if="status_change_to==='dead'">
                    <p>Please pick a reason below:</p>
                    <form-select
                        id="dead_status_reason"
                        name="dead_status_reason"
                        v-model="dead_status_reason"
                        :empty-value="0"
                        empty-label="(Pick a reason)"
                        :options="dead_reasons"
                        :editable="true"
                        :required="true"
                        class="uk-width-1-1"
                    />
                </div>

                <div class="uk-modal-footer uk-text-center">
                    <base-button
                        size="large"
                        width="1-1"
                        @click.prevent="cancel_status_change(true)"
                    >
                        No, forget it
                    </base-button>
                    <base-button
                        color="primary"
                        size="large"
                        width="1-1"
                        :disabled="!can_change_status"
                        :waiting="changing_status"
                        @click.prevent="execute_status_change"
                    >
                        Yes, change to "{{ project_status_label(status_change_to) }}"
                    </base-button>
                </div>
            </form>
        </modal-dialog>
    </tab-section>
</template>

<script type="text/javascript">
import orderBy from "lodash/orderBy"
import { mapGetters } from "vuex"

import is_project_tab_section from "../mixins/IsProjectTabSection"
import project_utilities from "../../../mixins/ProjectUtilities"
import handles_team_members from "../mixins/HandlesTeamMembers"
import string_utilities from "../../../mixins/StringUtilities"
import wizard_target from "../mixins/WizardTarget"

import AttachmentImage from "../../../ui/AttachmentImage"
import FormInput from "../../../ui/FormInput"
import FormCheckbox from "../../../ui/FormCheckbox"
import FormSelect from "../../../ui/FormSelect"
import FormSelectUser from "../../../ui/FormSelectUser"
import FormTextArea from "../../../ui/FormTextArea"
import ModalDialog from "../../../dialogs/ModalDialog/ModalDialog"
import BaseButton from "../../../ui/BaseButton"
import DropdownButton from "../../../ui/DropdownButton"
import OpenIcon from "../../../ui/OpenIcon"

export default {
    mixins: [is_project_tab_section, project_utilities, handles_team_members, string_utilities, wizard_target],
    components: {
        AttachmentImage,
        FormInput,
        FormCheckbox,
        FormSelect,
        FormSelectUser,
        FormTextArea,
        ModalDialog,
        BaseButton,
        DropdownButton,
        OpenIcon
    },
    watch: {
        activatable: "proceed_with_status_change"
    },
    methods: {
        toggle_studio (studio_id, checked) {
            const index = this.record.studio_ids.indexOf(studio_id)
            if(checked && (index < 0)) this.record.studio_ids.push(studio_id)
            else if(!checked && (index >= 0)) this.record.studio_ids.splice(index, 1)
            this.save_field(this.record.studio_ids, "studio_ids")
        },
        change_team (value, field, old_user_id = 0) {
            if(field === "pm_id") value = value || null
            else if(field === "designer_ids") {
                const designer_ids = [...(this.record.designer_ids || [])]
                const index = designer_ids.indexOf(old_user_id || value)
                if(index >= 0) designer_ids[index] = `${designer_ids[index]}->${value}`
                else if(value) designer_ids.push(value)
                value = designer_ids
            }
            this.save_field(value, field).then(() => {
                const team_member_ids = [
                    this.record.pic_id,
                    this.record.pm_id || 0,
                    ...(this.record.designer_ids || [])
                ]
                const project_users = this.entity_records("project_user").filter(project_user => project_user.project_id === this.record.id)
                const task_users = this.entity_records("task_user").filter(task_user => task_user.project_id === this.record.id)
                project_users.forEach(project_user => {
                    if(team_member_ids.indexOf(project_user.user_id) < 0) this.$store.dispatch("DUSTING", { entity: "project_user", record_id: project_user.id })
                })
                task_users.forEach(task_user => {
                    if(team_member_ids.indexOf(task_user.user_id) < 0) this.$store.dispatch("DUSTING", { entity: "task_user", record_id: task_user.id })
                })
            })
        },
        pics_filter (user) {
            return !!this.record && this.user_can_be_pic(user) && (this.record.designer_ids.indexOf(user.id) < 0)
        },
        pms_filter (user) {
            return !!this.record && this.user_can_be_pm(user, this.record.internal) && (this.record.pic_id !== user.id) && (this.record.designer_ids.indexOf(user.id) < 0)
        },
        revert_status () {
            this.$nextTick(() => {
                this.record.project_status = this.original_project_status
                this.original_project_status = ""
            })
        },
        change_status (new_status) {
            this.all_other_team_project_ids = []
            if(!!this.record && !!this.record.project_group_id) this.all_other_team_project_ids = this.entity_records("project").filter(project => (project.id !== this.record.id) && (project.project_group_id === this.record.project_group_id)).map(project => project.id)
            this.other_team_project_ids = [...this.all_other_team_project_ids]
            this.activate_as_scope = !!this.record.activation_scope_number
            this.scope_number = this.record.activation_scope_number || ""
            this.scope_suffix = ""

            this.original_project_status = this.record.project_status
            this.status_change_to = new_status
            this.record.project_status = new_status
            if(new_status.match(/^(active|dead|declined)$/i)) this.dead_status_reason = 0
            if(new_status.match(/^(active|dead|declined|other)$/i)) {
                if(this.record.activated_at) this.save_new_status(new_status)
                else if(this.activatable) {
                    if(new_status.match(/^(other)$/i)) this.save_new_status(new_status)
                    else this.confirming_status_change = true
                } else {
                    this.record.project_status = new_status
                    this.revert_status()

                    this.$emit("wizard", new_status)
                }
            } else this.save_new_status(new_status)
        },
        project_status_label (project_status) {
            return this.term_value_by(project_status, "project_status").replace(/^-\s+(.+)\s+-$/, "$1")
        },
        project_by_id (id) {
            return this.entity_records("project").find(project => project.id === id) || {}
        },
        toggle_other_team_project_id (id) {
            const index = this.other_team_project_ids.indexOf(id)
            if(index >= 0) this.other_team_project_ids.splice(index, 1)
            else this.other_team_project_ids.push(id)
        },
        cancel_status_change (revert = false) {
            this.$emit("status-change-done")
            this.status_change_to = ""
            this.confirming_status_change = false
            if(revert) this.revert_status()
            else this.held_project_status = ""
        },
        execute_status_change () {
            this.clear_all_errors()
            if(this.activate_as_scope && !this.scope_number) this.add_error("scope_number", "Please provide an existing project number.", false)
            else {
                // dead_status_reason
                let notes = ""
                if(this.status_change_to === "dead") notes = this.term_value(this.dead_status_reason, this.dead_reasons)
                this.save_new_status(this.status_change_to, notes)
            }
        },
        save_new_status (project_status, project_status_notes = "") {
            this.changing_status = true
            const data = {
                project_status,
                project_status_notes,
                other_team_projects: this.other_team_project_ids
            }
            if(this.activate_as_scope && this.scope_number) {
                data.scope_number = this.scope_number
                data.scope_suffix = this.scope_suffix
            }
            this.$store.dispatch("RECORD_SAVE", {
                entity: "project",
                id: this.record.id,
                data
            }).then(record => {
                if(project_status === "active") this.$router.replace({ name: "project.edit", params: { id: this.record.id } })
                this.$emit("status-changed", project_status)
                this.cancel_status_change()
            }).catch(error => {
                this.$error(error.message)
            }).then(() => {
                this.changing_status = false
            })
        },
        proceed_with_status_change () {
            if(this.record && !!this.status_change_to && this.activatable) {
                this.change_status(this.status_change_to)
            }
        },
        deactivate () {
            this.$confirm({
                type: "error",
                title: "Delete Comment",
                message: "Are you sure you want to revert this to a prospective project?",
                cancel: "No, leave it active",
                ok: "Yes, revert",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("DEACTIVATE_RECORD", {
                            entity: "project",
                            id: this.record.id
                        }).catch(error => {
                            if(error) this.$error(error.message)
                        }).then(() => {
                            this.$router.push({ name: "prospective_project.edit", params: { id: this.record.id } })
                        })
                    }
                }
            })
        },
        available_designers (user_id) {
            return orderBy(this.filtered_designers.filter(user => {
                return !user.deleted_at && ((user.id === user_id) || (this.record.designer_ids.indexOf(user.id) < 0))
            }), "initials", "asc")
        },
        designer_by_id (user_id) {
            return this.entity_records("user").find(user => (user.id === user_id)) || { initials: user_id }
        },
        project_user_by_id (user_id) {
            return this.entity_records("project_user").find(project_user => (project_user.project_id === this.record.id) && (project_user.user_id === user_id)) || {}
        },
        designer_toggle_star (user_id) {
            this.$store.dispatch("RECORD_ACTION",
                {
                    entity: "project",
                    id: this.record_id,
                    action: `user/${user_id}/toggle_star`,
                    method: "post"
                }
            ).catch(error => {
                this.$error("<strong>" + error.header + "</strong><br>" + error.message)
            })
        },
        remove_designer (user_id) {
            const designer = this.designer_by_id(user_id)
            if(!designer) return
            this.$confirm({
                type: "error",
                title: `Remove ${designer.initials}`,
                message: "Are you sure you want to remove '" + designer.initials + "' from this project? Any assigned time will be deleted also.",
                cancel: "Keep",
                ok: "Remove",
                then: (confirmed) => {
                    if(confirmed) this.change_team(0, "designer_ids", user_id)
                }
            })
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        raw_studio_records () {
            return this.entity_records("studio")
        },
        number_editable () {
            return this.record && this.record.internal && this.editable
        },
        project () {
            return this.record
        },
        pic () {
            return this.pic_for_project(this.record)
        },
        pm () {
            return this.pm_for_project(this.record)
        },
        sorted_designer_ids () {
            if(!this.record) return []
            return orderBy(this.record.designer_ids, (designer_id) => {
                const project_user = this.entity_records("project_user").find(project_user => (project_user.project_id === this.record.id) && (project_user.user_id === designer_id))
                const designer = this.designer_by_id(designer_id)
                if(!!project_user && project_user.starred) return `1.${designer.initials}`
                return `2.${designer.initials}`
            }, "asc")
        },
        filtered_designers () {
            if(!this.record) return []
            return this.raw_user_records.filter(user => (user.role <= (this.record.internal ? this.$root.constants.roles.ADMIN.id : this.$root.constants.roles.PRINCIPAL.id)) && (user.id !== this.record.pic_id) && (user.id !== this.record.pm_id))
        },
        prospective_project_statuses () {
            const project_statuses = this.dictionary("project_status", "name")
            let stati = project_statuses.filter((term) => {
                if(this.deactivating) {
                    if(term.code_constant.match(/^(chosen-)?pending$/i)) return true
                } else {
                    // if(term.code_constant.match(/^(on-hold|(chosen-)?pending|declined|other)$/i)) return true
                    if(term.code_constant.match(/^((chosen-)?pending|declined|other)$/i)) return true
                    if((term.code_constant.match(/^(active|dead)$/i)) && (this.is_business_manager || this.im_pic_or_pm || this.my_project || this.is_db_admin_active)) return true
                }
                return false
            })
            if(!this.deactivating) {
                stati = stati.map((term) => {
                    if((term.code_constant === "active") && !term.name.match(/-\s.+?\s-/)) term.name = "- " + term.name + " -"
                    return term
                })
            }
            return stati
        },
        project_statuses () {
            if(!this.record || !this.record.activated_at) return this.prospective_project_statuses

            const project_statuses = this.dictionary("project_status", "name")
            if(!this.status_editable) return project_statuses

            return project_statuses.filter((term) => {
                if(term.code_constant.match(/^(on-hold|active|completed)$/i)) return true
                if((term.code_constant.match(/^(dead)$/i)) && (this.is_business_manager || this.is_administrator || this.im_pic_or_pm || this.is_db_admin_active)) return true
                return false
            })
        },
        can_change_status () {
            if(!this.activatable && (this.status_change_to === "dead")) {
                if(this.has_required_data("category_ids") && !this.has_required_data("size")) {
                    const type_of_request = this.types_of_request.find(type_of_request => type_of_request.id === this.record.type_of_request)
                    if(type_of_request && (type_of_request.code_constant === "architect")) return true
                }
            }
            return this.activatable
        },
        dead_reasons () {
            return this.dictionary("dead_reasons")
        }
    },
    props: {
        activatable: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            confirming_status_change: false,
            original_project_status: "",
            status_change_to: "",
            dead_status_reason: 0,
            changing_status: false,

            all_other_team_project_ids: [],
            other_team_project_ids: [],
            activate_as_scope: false,
            scope_number: "",
            scope_suffix: ""
        }
    }
}
</script>
